import {CSSProperties, ForwardedRef, MutableRefObject, ReactNode, RefCallback, useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon, XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {useTranslation} from '@/translations/i18n'
import {genericForwardRef} from '@/utilities/helpers'
import {ComboboxItem, ComboboxProvider, SelectProvider} from '@ariakit/react'

interface InputSelectProps<T extends string = string> {
    onChange: (value: T) => void
    options: Array<{
        label: string
        value: T
    }>
    label?: string
    placeholder?: string
    value?: T
    errorMessage?: string
    displayValue?: (label: string) => ReactNode
    onClear?: () => void
    isOptional?: boolean
    sameWidth?: boolean
    disabled?: boolean
    disabledOptions?: T[]
    isPortal?: boolean
    portalRef?: RefCallback<HTMLElement> | MutableRefObject<HTMLElement | null>
    triggerClassName?: string
    hasItemCheckbox?: boolean
    style?: CSSProperties
    /*Allows selecting a value that is not present in the options, by typing it in the search field*/
    allowCustomValue?: boolean
    /*Caps the length of the text typed in the search field, to keep custom values within the backend limit*/
    customValueMaxLength?: number
}

export const InputSelectCombobox = genericForwardRef(
    <T extends string = string>(
        {
            label,
            value,
            placeholder,
            isOptional,
            onClear,
            displayValue,
            errorMessage,
            onChange,
            options,
            sameWidth,
            isPortal,
            portalRef,
            disabled,
            disabledOptions,
            triggerClassName,
            hasItemCheckbox = true,
            style,
            allowCustomValue = false,
            customValueMaxLength
        }: InputSelectProps<T>,
        ref: ForwardedRef<HTMLSelectElement>
    ) => {
        const {t} = useTranslation()
        const [search, setSearch] = useState('')
        const trimmedSearch = search.trim()
        /*A custom value already selected is not part of the options, so we add it to keep it visible and selected*/
        const selectableOptions =
            allowCustomValue && value && !options.some(option => option.value === value)
                ? [...options, {label: value, value}]
                : options
        const canCreateCustomValue =
            allowCustomValue && !!trimmedSearch && !selectableOptions.some(option => option.value === trimmedSearch)

        return (
            <ComboboxProvider value={search ?? ''} setValue={setSearch}>
                <SelectProvider value={value ?? ''} setValue={onChange}>
                    <Flexbox direction="column" gap={1.5} fullWidth style={style}>
                        {label && (
                            <Label>
                                {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                            </Label>
                        )}
                        <SelectTrigger
                            ref={ref}
                            className={triggerClassName}
                            placeholder={placeholder ?? t('commons:select')}
                            value={value}
                            fullWidth
                            disabled={disabled}
                            hasError={!!errorMessage}
                            displayValue={value =>
                                typeof value == 'string' && (
                                    <Flexbox justify="space-between" align="center" fullWidth gap={2}>
                                        {displayValue
                                            ? displayValue(
                                                  selectableOptions.find(option => option.value === value)?.label ?? ''
                                              )
                                            : selectableOptions.find(option => option.value === value)?.label ?? value}
                                        {onClear && <XCloseIcon role="button" onClick={onClear} />}
                                    </Flexbox>
                                )
                            }
                        />
                        {errorMessage && (
                            <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}
                        <SelectComboboxPopover portal={!!isPortal} portalRef={portalRef} sameWidth={sameWidth}>
                            <SelectComboboxInput maxLength={allowCustomValue ? customValueMaxLength : undefined} />
                            <SelectComboboxList>
                                {selectableOptions
                                    .filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
                                    .map((option, index) => (
                                        <SelectItem
                                            disabled={disabledOptions?.includes(option.value)}
                                            hasItemCheckbox={hasItemCheckbox}
                                            key={index}
                                            value={option.value}
                                            render={<ComboboxItem />}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                {canCreateCustomValue && (
                                    <SelectItem hasItemCheckbox={false} value={trimmedSearch} render={<ComboboxItem />}>
                                        <Flexbox align="center" gap={1.5}>
                                            <PlusIcon size={16} />
                                            {t('commons:add_option', {value: trimmedSearch})}
                                        </Flexbox>
                                    </SelectItem>
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
